import { CaretDownIcon, RecordIcon } from "@phosphor-icons/react";
import { Button } from "antd";
import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { radius } from "@libertify/ui";

export const CameraWithMask = ({
  onPhotoCapture,
  onCancel,
}: {
  onPhotoCapture?: (blob: Blob) => void;
  onCancel?: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const startCamera = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user", // Front camera
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
        }
      } catch (error) {
        console.error("Camera access denied:", error);
      }
    };

    void startCamera();

    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) return;

    // Set canvas size to match the circular mask (square for circle)
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = size;
    canvas.height = size;

    // Calculate the center for the square crop
    const centerX = video.videoWidth / 2;
    const centerY = video.videoHeight / 2;
    const halfSize = size / 2;

    context.save();

    // Mirror the image back to normal orientation during capture
    context.scale(-1, 1); // Flip horizontally to undo the CSS mirror
    context.translate(-size, 0); // Adjust position after flip

    // Draw the video frame centered and cropped to square
    const sourceX = centerX - halfSize;
    const sourceY = centerY - halfSize;
    const sourceSize = size;

    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceSize,
      sourceSize,
      0,
      0,
      size,
      size
    );

    context.restore();

    // Convert to blob
    canvas.toBlob(
      (blob) => {
        if (blob) {
          console.log("Photo captured:", blob);
          onPhotoCapture?.(blob);
        }
      },
      "image/jpeg",
      0.8
    );
  }, [onPhotoCapture]);

  return (
    <CameraContainer>
      <CaretDownButton
        onClick={onCancel}
        size="large"
        icon={<CaretDownIcon size={30} weight="bold" />}
        type="text"
        shape="circle"
        style={{
          zIndex: 20,
        }}
      />
      <CameraVideo ref={videoRef} autoPlay playsInline muted />
      <MaskOverlay />
      <Button
        onClick={capturePhoto}
        style={{
          zIndex: 20,
          position: "absolute",
          bottom: 50,
          left: "50%",
          transform: "translateX(-50%)",
          border: "none",
          background: "transparent",
        }}
      >
        <RecordIcon color={"#ffffff"} weight="fill" size={80} />
      </Button>
      <canvas ref={canvasRef} style={{ display: "none" }} />
    </CameraContainer>
  );
};

const CameraContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100dvh;
  background: #000;
`;

const CameraVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(
    -1
  ); /* Mirror the video feed for natural selfie experience */
`;

const MaskOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: min(100dvw - 100px, 400px);
    height: min(calc(100dvw - 60px) * 1.3, 520px, calc(100dvh - 280px));
    border: 2px solid #fff;
    border-radius: ${radius.circle};
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
  }
`;

const CaretDownButton = styled(Button)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 30px;
  width: 60px !important;
  height: 60px !important;
  background-color: #00000010;
`;
